/**
 * Product Recommendation API Service
 *
 * Integrates with backend LlamaIndex-powered recommendation engine
 * to provide real-time insurance product recommendations based on
 * conversation context.
 */

import type { InsuranceProduct } from '@/data/insurance-products';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE || 'https://aibot.hupo.co';

export interface RecommendationRequest {
  scenarioCode: string;
  conversationHistory: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: string;
  }>;
  clientPersona: {
    age: number;
    occupation: string;
    concerns: string[];
  };
  version?: number; // Optional API version (1, 2, 3, etc.) - defaults to 1
}

// Product Comparison Types (for Mode C)
export interface ComparisonMetric {
  metric_name: string;
  metric_name_ko: string;
  aia_value: string;
  competitor_value: string;
  aia_advantage: boolean;
  explanation: string;
  explanation_ko: string;
}

export interface ProductComparison {
  competitor_name: string;
  competitor_name_ko: string;
  comparison_metrics: ComparisonMetric[];
  talking_points: string[];
  talking_points_ko: string[];
  overall_recommendation: string;
  overall_recommendation_ko: string;
}

export interface RecommendationResponse {
  mode: 'no_action' | 'recommend' | 'compare'; // NEW: Response mode
  shouldUpdate: boolean;
  products: InsuranceProduct[];
  comparisons?: ProductComparison[]; // NEW: Comparison data for Mode C
  analysisMetadata: {
    detectedConcerns: string[];
    detectedCompetitors?: string[]; // NEW: For Mode C
    conversationContext: string;
    lastUpdated: string;
  };
}

/**
 * Analyze conversation and get product recommendations
 *
 * This function calls the backend API which uses advanced pipeline:
 * 1. Conversation Analysis (GPT-4o-mini with structured outputs)
 * 2. Hybrid Retrieval (LlamaIndex: dense + sparse with dynamic alpha)
 * 3. Semantic Reranking (LLM-based contextual ranking)
 * 4. Bilingual Reasoning (Personalized English + Korean content)
 *
 * Backend supports multiple versions:
 * - v1: https://aibot.hupo.co/api/roleplay/recommendations/analyze (default)
 * - v2: https://aibot.hupo.co/api/roleplay/recommendations/analyze/v2
 * - v3: https://aibot.hupo.co/api/roleplay/recommendations/analyze/v3
 *
 * Error handling: On error (HTTP 500 with { "error": "message" }),
 * the frontend keeps previous recommendations instead of clearing them.
 */
export async function getProductRecommendations(
  request: RecommendationRequest,
  signal?: AbortSignal
): Promise<RecommendationResponse> {
  // Determine API version (default to 1)
  const version = request.version || 1;

  // Build endpoint URL based on version
  const endpoint = version === 1
    ? `${API_BASE_URL}/api/roleplay/recommendations/analyze`
    : `${API_BASE_URL}/api/roleplay/recommendations/analyze/v${version}`;

  console.log('🔄 Calling recommendation API:', {
    version,
    url: endpoint,
    scenarioCode: request.scenarioCode,
    conversationLength: request.conversationHistory.length,
  });

  try {
    const response = await fetch(
      endpoint,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal,
      }
    );

    // Handle error responses (HTTP 500 with { "error": "message" })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

      console.error('❌ API error response:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });

      // Extract error message from backend format
      const errorMessage = typeof errorData.error === 'string'
        ? errorData.error
        : errorData.error?.message || errorData.message || 'API request failed';

      throw new Error(errorMessage);
    }

    const data: RecommendationResponse = await response.json();

    console.log('✅ Recommendation API success:', {
      shouldUpdate: data.shouldUpdate,
      productCount: data.products?.length || 0,
      detectedConcerns: data.analysisMetadata?.detectedConcerns || [],
    });

    return data;
  } catch (error) {
    // Handle abort
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('⚠️ Recommendation request aborted');
      throw error;
    }

    console.error('❌ Product recommendation API error:', error);

    // Re-throw with better error message for UI
    throw new Error(
      error instanceof Error
        ? error.message
        : 'Failed to fetch product recommendations'
    );
  }
}

/**
 * Check if conversation warrants a recommendation update
 *
 * This is a lightweight endpoint that quickly determines if
 * we should fetch new recommendations without the heavy lifting
 * of actually generating them.
 */
export async function shouldUpdateRecommendations(
  request: Omit<RecommendationRequest, 'clientPersona'>,
  signal?: AbortSignal
): Promise<boolean> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/roleplay/recommendations/should-update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal,
      }
    );

    if (!response.ok) {
      console.warn('Should-update check failed, defaulting to true');
      return true;
    }

    const data = await response.json();
    return data.shouldUpdate ?? true;
  } catch (error) {
    // On error, default to updating (fail-safe)
    console.warn('Should-update check error, defaulting to true:', error);
    return true;
  }
}
